import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { DEFAULT_OPERATOR_BY_FIELD_TYPE } from '@/views/program/scopeFilter/constants';
import type {
  Combinator,
  ConditionNode,
  ConditionValue,
  FieldType,
  GroupNode,
  RuleNode,
  ConditionOperator,
} from '@/views/program/scopeFilter/types';

const makeId = () => crypto.randomUUID();

export const createCondition = (id: string): ConditionNode => ({
  kind: 'condition',
  id,
  fieldId: null,
  operator: null,
  value: null,
});

const createGroup = (
  id: string,
  children: RuleNode[],
  combinator: Combinator = 'and'
): GroupNode => ({ kind: 'group', id, combinator, children });

export const createInitialTree = (id: string): GroupNode => createGroup(id, []);

interface ScopeFilterState {
  tree: GroupNode;
  /** A condition is "touched" once the user has interacted with and left it. */
  touched: Record<string, boolean>;
  /** View mode: the form renders greyed and non-editable (set from the plan's edit mode). */
  readOnly: boolean;
}

const initialState: ScopeFilterState = {
  tree: createInitialTree(makeId()),
  touched: {},
  readOnly: false,
};

/** Depth-first lookup over the tree. */
const findNode = (node: RuleNode, id: string): RuleNode | undefined => {
  if (node.id === id) return node;
  if (node.kind === 'group') {
    for (const child of node.children) {
      const found = findNode(child, id);
      if (found) return found;
    }
  }
  return undefined;
};

const findGroup = (root: GroupNode, id: string): GroupNode | undefined => {
  const node = findNode(root, id);
  return node?.kind === 'group' ? node : undefined;
};

const findCondition = (
  root: GroupNode,
  id: string
): ConditionNode | undefined => {
  const node = findNode(root, id);
  return node?.kind === 'condition' ? node : undefined;
};

/** Removes `id` from the tree and prunes any group left empty by that removal,
 *  so deleting a group's last condition drops the (now-stale) group too. Only
 *  groups on the removal path are pruned — an unrelated empty group is kept — and
 *  the root group is never removed. Returns whether the id was found here. */
const removeFromTree = (group: GroupNode, id: string): boolean => {
  const index = group.children.findIndex((child) => child.id === id);
  if (index !== -1) {
    group.children.splice(index, 1);
    return true;
  }
  for (const child of group.children) {
    if (child.kind === 'group' && removeFromTree(child, id)) {
      if (child.children.length === 0) {
        group.children = group.children.filter((c) => c.id !== child.id);
      }
      return true;
    }
  }
  return false;
};

/** All condition ids within a node (the node itself plus any descendants). */
const conditionIdsIn = (node: RuleNode): string[] =>
  node.kind === 'condition' ? [node.id] : node.children.flatMap(conditionIdsIn);

const scopeFilterSlice = createSlice({
  name: 'scopeFilter',
  initialState,
  reducers: {
    addCondition: {
      prepare: ({ parentId }: { parentId: string }) => ({
        payload: { parentId, id: makeId() },
      }),
      reducer: (
        state,
        action: PayloadAction<{ parentId: string; id: string }>
      ) => {
        findGroup(state.tree, action.payload.parentId)?.children.push(
          createCondition(action.payload.id)
        );
      },
    },
    wrapInGroup: {
      prepare: ({ conditionId }: { conditionId: string }) => ({
        payload: { conditionId, groupId: makeId() },
      }),
      reducer: (
        state,
        action: PayloadAction<{ conditionId: string; groupId: string }>
      ) => {
        // Grouping a condition moves it into a new group in its place (rather than
        // creating an empty group beside it). Depth is capped at the root, so the
        // condition being grouped is always a direct child of the root.
        const index = state.tree.children.findIndex(
          (child) => child.id === action.payload.conditionId
        );
        const condition = state.tree.children[index];
        if (index === -1 || condition.kind !== 'condition') return;
        state.tree.children[index] = createGroup(action.payload.groupId, [
          condition,
        ]);
      },
    },
    removeNode: (state, action: PayloadAction<{ id: string }>) => {
      const target = findNode(state.tree, action.payload.id);
      if (target) {
        for (const id of conditionIdsIn(target)) delete state.touched[id];
      }
      removeFromTree(state.tree, action.payload.id);
    },
    updateField: (
      state,
      action: PayloadAction<{
        id: string;
        fieldId: string;
        fieldType: FieldType;
      }>
    ) => {
      const condition = findCondition(state.tree, action.payload.id);
      if (!condition || condition.fieldId === action.payload.fieldId) return;
      condition.fieldId = action.payload.fieldId;
      condition.operator =
        DEFAULT_OPERATOR_BY_FIELD_TYPE[action.payload.fieldType];
      condition.value = null;
    },
    updateOperator: (
      state,
      action: PayloadAction<{ id: string; operator: ConditionOperator }>
    ) => {
      const condition = findCondition(state.tree, action.payload.id);
      if (condition) condition.operator = action.payload.operator;
    },
    updateValue: (
      state,
      action: PayloadAction<{ id: string; value: ConditionValue }>
    ) => {
      const condition = findCondition(state.tree, action.payload.id);
      if (condition) condition.value = action.payload.value;
    },
    setCombinator: (
      state,
      action: PayloadAction<{ groupId: string; combinator: Combinator }>
    ) => {
      const group = findGroup(state.tree, action.payload.groupId);
      if (group) group.combinator = action.payload.combinator;
    },
    markTouched: (state, action: PayloadAction<string>) => {
      state.touched[action.payload] = true;
    },
    // Dissolves a single-condition group, moving its condition back to the root in
    // the group's place (the inverse of `wrapInGroup`).
    unwrapGroup: (state, action: PayloadAction<{ groupId: string }>) => {
      const index = state.tree.children.findIndex(
        (child) => child.id === action.payload.groupId
      );
      const group = state.tree.children[index];
      if (index === -1 || group.kind !== 'group' || group.children.length !== 1)
        return;
      const [only] = group.children;
      if (only.kind !== 'condition') return;
      state.tree.children[index] = only;
    },
    setReadOnly: (state, action: PayloadAction<boolean>) => {
      state.readOnly = action.payload;
    },
    // Replaces the whole tree (used to hydrate a saved filter on plan load). The
    // tree is already complete, so nothing is marked touched.
    setScopeTree: (state, action: PayloadAction<GroupNode>) => {
      state.tree = action.payload;
      state.touched = {};
    },
    // `readOnly` is owned by the plan's edit mode, so it survives a reset.
    resetScopeFilter: (state) => ({
      ...initialState,
      readOnly: state.readOnly,
    }),
  },
});

export const {
  addCondition,
  wrapInGroup,
  removeNode,
  updateField,
  updateOperator,
  updateValue,
  setCombinator,
  markTouched,
  unwrapGroup,
  setReadOnly,
  setScopeTree,
  resetScopeFilter,
} = scopeFilterSlice.actions;

export const { getInitialState } = scopeFilterSlice;

export default scopeFilterSlice.reducer;
